import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, MessageCircle, User, Bot, CheckCircle, Brain } from 'lucide-react';
import { InterviewMessage } from '../types/chat';
import { ContentPlanInterviewService, ContentPlanFormData } from '../services/contentPlanInterviewService';
import { useAuth } from '../context/AuthContext';

interface InterviewChatProps {
  isOpen: boolean;
  onClose: () => void;
  onInterviewCompleted: (data: ContentPlanFormData) => void;
}

export function InterviewChat({ isOpen, onClose, onInterviewCompleted }: InterviewChatProps) {
  const { token } = useAuth();
  const [messages, setMessages] = useState<InterviewMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [interviewId, setInterviewId] = useState<string>('');
  const [connectionStatus, setConnectionStatus] = useState<string>('disconnected');
  const [historyLoaded, setHistoryLoaded] = useState<boolean>(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const interviewServiceRef = useRef<ContentPlanInterviewService | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Блокировка скролла страницы при открытии окна интервью
  useEffect(() => {
    if (isOpen) {
      // Сохраняем текущую позицию скролла
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Восстанавливаем скролл при закрытии
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Очищаем стили при размонтировании компонента
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen]);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  useEffect(() => {
    if (!isOpen || !token) return;

    const initInterview = async () => {
      try {
        
        // Сбрасываем флаг истории
        setHistoryLoaded(false);
        
        // Создаем и инициализируем сервис интервью для контент-плана
        const service = new ContentPlanInterviewService(token);
        interviewServiceRef.current = service;

        
        // Подключаемся к хабу
        await service.connect();
        
        // Регистрируем обработчики событий
        const unsubscribeMessage = service.onMessage((message) => {
          
          // Улучшенная проверка дубликатов
          setMessages(prev => {
            // Проверяем, существует ли уже сообщение с таким ID
            const existsById = prev.some(m => m.id === message.id);
            if (existsById) {
              return prev;
            }
            
            // Если история уже загружена и это сообщение ИИ, проверяем более строго
            if (historyLoaded && message.isFromAI) {
              const similarExists = prev.some(m => 
                m.isFromAI === message.isFromAI && 
                m.content === message.content
              );
              if (similarExists) {
                return prev;
              }
            }
            
            // Обычная проверка на дубликаты по содержанию и времени (только для ИИ сообщений)
            if (message.isFromAI) {
              const similarExists = prev.some(m => 
                m.isFromAI === message.isFromAI && 
                m.content === message.content &&
                Math.abs(m.timestamp.getTime() - message.timestamp.getTime()) < 10000 // 10 секунд
              );
              if (similarExists) {
                return prev;
              }
            }
            
            // Если сообщение уникальное, добавляем его
            return [...prev, message];
          });

          // Если сообщение от ИИ, устанавливаем статус готовности к вводу
          if (message.isFromAI) {
            setConnectionStatus('ready');
            setIsTyping(false);
          }
        });

        const unsubscribeStatus = service.onStatusChange((status) => {
          handleStatusChange(status);
        });
        
        const unsubscribeCompleted = service.onInterviewCompleted((taskId) => {
          setIsCompleting(false);
          onInterviewCompleted(taskId);
          onClose();
        });

        // Проверяем, есть ли существующее интервью
        const hasExistingInterview = await loadExistingInterview(service);
        
        // Если нет существующего интервью, начинаем новое
        if (!hasExistingInterview) {
          await service.startInterview();
        } else {
        }
      } catch (error) {
        console.error('[InterviewChat] Ошибка при инициализации интервью:', error);
        setConnectionStatus('error');
      }
    };

    initInterview();

    // Очистка при закрытии чата
    return () => {
      if (interviewServiceRef.current) {
        interviewServiceRef.current.disconnect();
      }
    };
  }, [isOpen, token]);

  const loadExistingInterview = async (service: ContentPlanInterviewService): Promise<boolean> => {
    try {
      
      // Проверяем, есть ли активное интервью
      const activeInterviewId = await service.loadActiveInterview();
      
      if (activeInterviewId) {
        setInterviewId(activeInterviewId);
        
        // Очищаем существующие сообщения перед загрузкой истории
        setMessages([]);
        
        // Загружаем историю сообщений
        const messages = await service.loadInterviewHistory(activeInterviewId);
        
        // Добавляем сообщения в состояние, если они есть
        if (messages.length > 0) {
          // Фильтруем дубликаты при загрузке истории
          setMessages(prevMessages => {
            const uniqueMessages = messages.filter(newMsg => 
              !prevMessages.some(existingMsg => 
                existingMsg.id === newMsg.id ||
                (existingMsg.content === newMsg.content && 
                 existingMsg.isFromAI === newMsg.isFromAI)
              )
            );
            return [...prevMessages, ...uniqueMessages];
          });
        }
        
        setHistoryLoaded(true); // Устанавливаем флаг после загрузки истории
        setConnectionStatus('ready');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[InterviewChat] Ошибка при загрузке существующего интервью:', error);
      return false;
    }
  };

  const startInterview = async () => {
    if (!token) {
      return;
    }

    setIsConnecting(true);
    setMessages([]);

    try {
      // Создаем сервис интервью для контент-плана
      const service = new ContentPlanInterviewService(token);
      interviewServiceRef.current = service;

      // Подписываемся на события
      const unsubscribeMessage = service.onMessage((message) => {
        // Проверяем, есть ли уже такое сообщение в списке
        setMessages(prev => {
          // Проверяем, существует ли уже сообщение с таким ID
          const exists = prev.some(m => m.id === message.id);
          if (exists) {
            return prev; // Возвращаем массив без изменений
          }
          // Проверяем, есть ли похожее сообщение по содержанию и времени
          const similarExists = prev.some(m => 
            m.content === message.content && 
            m.isFromAI === message.isFromAI && 
            Math.abs(m.timestamp.getTime() - message.timestamp.getTime()) < 5000
          );
          if (similarExists) {
            return prev; // Возвращаем массив без изменений
          }
          
          // Если сообщение уникальное, добавляем его
          return [...prev, message];
        });

        // Если сообщение от ИИ, устанавливаем статус готовности к вводу
        if (message.isFromAI) {
          setConnectionStatus('ready');
          setIsTyping(false);
        }
      });

      const unsubscribeStatus = service.onStatusChange((status) => {
        setConnectionStatus(status);
        
        if (status === 'typing') {
          setIsTyping(true);
        } else if (status === 'idle') {
          setIsTyping(false);
        } else if (status === 'started') {
          const currentId = service.getInterviewId();
          if (currentId) {
            setInterviewId(currentId);
          }
        } else if (status === 'cancelled') {
          // Обработка отмены интервью
          setInterviewId('');
        }
      });

      const unsubscribeCompleted = service.onInterviewCompleted((taskId) => {
        setIsCompleting(false);
        onInterviewCompleted(taskId);
        onClose();
      });

      // Подключаемся к Hub
      await service.connect();
      
      // Проверяем, есть ли существующее интервью
      const hasExistingInterview = await loadExistingInterview(service);
      
      // Если нет существующего интервью, начинаем новое
      if (!hasExistingInterview) {
        await service.startInterview();
        // Получаем ID после запуска интервью
        const newInterviewId = service.getInterviewId();
        if (newInterviewId) {
          setInterviewId(newInterviewId);
        }
      }

    } catch (error) {
      console.error('Ошибка при начале интервью:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const cleanup = () => {
    if (interviewServiceRef.current) {
      interviewServiceRef.current.disconnect();
      interviewServiceRef.current = null;
    }
    setMessages([]);
    setConnectionStatus('disconnected');
    setIsTyping(false);
    setInterviewId('');
    setIsCompleting(false);
  };

  const sendMessage = async () => {
    
    if (!inputValue.trim() || !interviewServiceRef.current) {
      return;
    }

    // Проверяем, что у нас есть ID интервью
    const currentInterviewId = interviewId || 
                               interviewServiceRef.current.getInterviewId() || 
                               localStorage.getItem('interview_id') || '';
    
    if (!currentInterviewId) {
      return;
    }

    const userMessage: InterviewMessage = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      isFromAI: false,
      timestamp: new Date()
    };

    // Добавляем сообщение пользователя локально
    setMessages(prev => [...prev, userMessage]);
    
    const messageText = inputValue.trim();
    setInputValue('');

    try {
      // Отправляем сообщение через SignalR
      await interviewServiceRef.current.sendMessage(currentInterviewId, messageText);
    } catch (error) {
      console.error('[InterviewChat] Ошибка отправки сообщения:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCompleteInterview = async () => {
    if (!interviewServiceRef.current || isCompleting) return;

    // Получаем ID интервью из сервиса или из состояния
    const currentInterviewId = interviewId || 
                               interviewServiceRef.current.getInterviewId() || 
                               localStorage.getItem('interview_id') || '';
    
    if (!currentInterviewId) {
      console.error('Не удалось получить ID интервью для завершения');
      return;
    }

    // Устанавливаем состояние загрузки
    setIsCompleting(true);

    // Завершаем интервью
    try {
      await interviewServiceRef.current.completeInterview(currentInterviewId);
      // Обработка завершения будет выполнена в onInterviewCompleted callback
    } catch (error) {
      console.error('Ошибка при завершении интервью:', error);
      // Сбрасываем состояние загрузки при ошибке
      setIsCompleting(false);
    }
  };

  const handleClearInterview = async () => {
    if (!interviewServiceRef.current) return;

    try {
      // Если есть активное интервью, отменяем его
      if (interviewId) {
        await interviewServiceRef.current.cancelInterview(interviewId);
      }
      
      // Сбрасываем ID интервью
      if (interviewServiceRef.current.resetInterviewId) {
        interviewServiceRef.current.resetInterviewId();
      }
      
      // Очищаем состояние
      setMessages([]);
      setInterviewId('');
      setConnectionStatus('ready');
      setIsCompleting(false);
      
      // Запускаем новое интервью
      await startInterview();
      
    } catch (error) {
      console.error('Ошибка при сбросе интервью:', error);
    }
  };

  // Обработчик изменения статуса интервью
  const handleStatusChange = useCallback((status: string) => {
    
    setConnectionStatus(status);

    if (status === 'typing') {
      setIsTyping(true);
    } else if (status === 'idle') {
      setIsTyping(false);
    } else if (status === 'error') {
      console.error('[InterviewChat] Ошибка интервью');
    } else if (status === 'cancelled') {
      // Обработка отмены интервью
      setInterviewId('');
      console.log('[InterviewChat] Интервью отменено');
    } else if (status === 'started') {
      // Получаем ID из сервиса с небольшой задержкой, чтобы убедиться что он установлен
      setTimeout(() => {
        const currentId = interviewServiceRef.current?.getInterviewId();
        if (currentId) {
          setInterviewId(currentId);
          setConnectionStatus('ready');
        }
      }, 200);
    } else if (status === 'ready') {
      // Убеждаемся, что у нас есть interviewId
      const currentId = interviewServiceRef.current?.getInterviewId();
      if (currentId && !interviewId) {
        setInterviewId(currentId);
      }
    }
  }, [interviewId]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-0 sm:p-4 z-[9998] interview-chat-modal">
      <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-2xl shadow-2xl w-full max-w-4xl h-full max-h-[calc(100dvh-64px)] sm:h-[calc(90vh-80px)] flex flex-col overflow-hidden md:mb-0 mb-16">
        {/* Заголовок */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 mb-2 sm:mb-0">
              <Brain className="w-6 h-6 text-blue-500" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Интервью
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  ИИ поможет заполнить поля
                </p>
              </div>
            </div>
          <div className="flex space-x-2 w-full sm:w-auto justify-between sm:justify-end">
            <button
              onClick={handleCompleteInterview}
              disabled={messages.length < 5 || isCompleting}
              className={`group flex items-center px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-md transform -translate-y-0.5 transition-all duration-200 font-medium rounded-lg transition-all duration-200 hover:shadow-md ${
                messages.length < 5 || isCompleting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isCompleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Обработка...
                </>
              ) : (
                'Заполнить поля'
              )}
            </button>
            <button
              onClick={handleClearInterview}
              className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors flex-1 sm:flex-none"
            >
              Очистить чат
            </button>
            <button
              onClick={onClose}
              className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors flex-1 sm:flex-none"
            >
              Закрыть окно
            </button>
          </div>
        </div>

        {/* Сообщения */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isConnecting ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Подключение...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <Bot className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Интервью
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  ИИ поможет заполнить поля
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Идет подключение к ИИ-ассистенту...
                </p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <div
                  key={message.id || `message-${index}`}
                  className={`flex ${message.isFromAI ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`flex items-start space-x-2 max-w-sm lg:max-w-xl ${
                      message.isFromAI ? 'flex-row' : 'flex-row-reverse space-x-reverse'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {message.isFromAI ? (
                        <Bot className="w-6 h-6 text-blue-500" />
                      ) : (
                        <User className="w-6 h-6 text-gray-500" />
                      )}
                    </div>
                    <div
                      className={`px-4 py-2 rounded-lg ${
                        message.isFromAI
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'bg-blue-500 text-white'
                      }`}
                    >
                      <div className="text-sm whitespace-pre-wrap">
                        {(message.content || '').split('\n').map((line, index) => (
                          <div key={`${message.id || `msg-${index}`}-line-${index}`}>
                            {line || '\u00A0'}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs mt-1 opacity-70">
                        {message.timestamp && !isNaN(message.timestamp.getTime()) 
                          ? message.timestamp.toLocaleTimeString() 
                          : 'Время неизвестно'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="flex items-start space-x-2">
                    <Bot className="w-6 h-6 text-blue-500" />
                    <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg">
                      <div className="flex space-x-1">
                        {[1, 2, 3].map((dot) => (
                          <div 
                            key={`typing-dot-${dot}`} 
                            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" 
                            style={{ animationDelay: `${(dot - 1) * 0.1}s` }}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Поле ввода */}
        <div className="p-2">
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Введите ваш ответ..."
                disabled={isConnecting || (connectionStatus !== 'connected' && connectionStatus !== 'started' && connectionStatus !== 'ready' && connectionStatus !== 'idle')}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg 
                        bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base
                        focus:outline-none focus:ring-2 focus:ring-blue-500
                        disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed
                        resize-none min-h-[40px] max-h-[120px] overflow-y-auto"
                style={{
                  minHeight: '40px',
                  maxHeight: '120px'
                }}
              />
            </div>
            <div className="flex-shrink-0 self-center">
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isConnecting || (connectionStatus !== 'connected' && connectionStatus !== 'started' && connectionStatus !== 'ready' && connectionStatus !== 'idle')}
                className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 
                        disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors
                        flex items-center justify-center h-10 w-10"
              >
                <Send className="w-5 h-5 -translate-x-px" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 